import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { gradeLevelActions } from "../slices/gradeLevel";
import { getErrorMessage } from "../../utils/errorUtils";
import { GradeLevelRequestDto } from "../../shared/dtos/gradeLevel/GradeLevelRequestDto";
import { GradeLevelResponseDto } from "../../shared/dtos/gradeLevel/GradeLevelResponseDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export const createGradeLevel =
  (values: GradeLevelRequestDto) => async (dispatch: AppDispatch) => {
    dispatch(gradeLevelActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<GradeLevelResponseDto>
      >("/grade-levels", values, { headers: { requiresAuth: true } });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Grade level created successfully!");
        dispatch(gradeLevelActions.createSuccess(data.data));
        dispatch(
          gradeLevelActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(gradeLevelActions.setErrorCreate(message));
    }
  };

export const getGradeLevels = () => async (dispatch: AppDispatch) => {
  dispatch(gradeLevelActions.setLoadingFetchAll());
  try {
    const { data } = await axiosInstance.get<
      ApiResponseDto<GradeLevelResponseDto[]>
    >("/grade-levels", { headers: { requiresAuth: true } });

    if (data.status === ApiResponseStatus.SUCCESS && data.data) {
      dispatch(gradeLevelActions.setItems(data.data));
    }
  } catch (error) {
    dispatch(gradeLevelActions.setErrorFetchAll(getErrorMessage(error)));
  }
};

export const getGradeLevelById =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(gradeLevelActions.setLoadingFetchOne());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<GradeLevelResponseDto>
      >(`/grade-levels/${id}`, { headers: { requiresAuth: true } });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(gradeLevelActions.setItem(data.data));
      }
    } catch (error) {
      dispatch(gradeLevelActions.setErrorFetchOne(getErrorMessage(error)));
    }
  };

export const updateGradeLevel =
  (payload: { id: string; values: GradeLevelRequestDto }) =>
  async (dispatch: AppDispatch) => {
    dispatch(gradeLevelActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<GradeLevelResponseDto>
      >(`/grade-levels/${payload.id}`, payload.values, {
        headers: { requiresAuth: true },
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Grade level updated successfully!");
        dispatch(gradeLevelActions.updateSuccess(data.data));
        dispatch(
          gradeLevelActions.setServerStatus({
            operation: "update",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(gradeLevelActions.setErrorUpdate(message));
    }
  };

export const deleteGradeLevel =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(gradeLevelActions.setLoadingDelete());
    try {
      const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
        `/grade-levels/${id}`,
        { headers: { requiresAuth: true } },
      );

      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Grade level deleted successfully!");
        dispatch(gradeLevelActions.deleteSuccess(id));
        dispatch(
          gradeLevelActions.setServerStatus({
            operation: "delete",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(gradeLevelActions.setErrorDelete(message));
    }
  };

export const resetGradeLevelServerStatus = () => (dispatch: AppDispatch) => {
  dispatch(gradeLevelActions.setServerStatus(null));
};
